import { useState, useCallback, useMemo } from 'react';
import { DocumentManager } from '../../document/services/DocumentManager.js'; // Adjust path
/**
 * Hook to load text document content (paragraphs).
 * Utilizes DocumentManager to fetch document data.
 */
export function useTextDocumentContent() {
    const [paragraphs, setParagraphs] = useState(null);
    const [rawParagraphs, setRawParagraphs] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    // Store the identity for which content is being loaded or was loaded
    const [, setCurrentIdentity] = useState(null);
    const documentManager = DocumentManager.getInstance(); // Get DocumentManager instance
    const loadContent = useCallback(async (identity) => {
        setCurrentIdentity(identity);
        if (!identity) {
            setParagraphs(null);
            setRawParagraphs(null);
            setIsLoading(false);
            setError(null);
            return;
        }
        // Avoid re-loading if already loading for the same identity or if paragraphs for this identity are already loaded.
        // This simple check might need refinement if paragraphs could be empty for a valid loaded doc.
        // However, DocumentManager cache handles the primary caching.
        // if (isLoading && currentIdentity?.id === identity.id && currentIdentity?.type === identity.type) {
        //   console.debug('[useTextDocumentContent] Already loading for identity:', identity.id);
        //   return;
        // }
        // if (paragraphs && currentIdentity?.id === identity.id && currentIdentity?.type === identity.type) {
        //  console.debug('[useTextDocumentContent] Paragraphs already loaded for identity:', identity.id);
        //  return;
        // }
        setIsLoading(true);
        setError(null);
        // Set paragraphs to null when starting a new load for a different identity
        // to avoid showing stale data briefly.
        // setParagraphs(null); // Or only if identity changes significantly.
        try {
            const result = await documentManager.loadDocument(identity);
            // DocumentManager.loadDocument now returns DocumentLoadResult which includes paragraphs
            if (result && result.paragraphs) {
                setParagraphs(result.paragraphs);
                setRawParagraphs(result.rawParagraphs || null);
            }
            else {
                // This case should ideally be handled by an error from loadDocument if doc not found
                // or if paragraphs are missing when they shouldn't be.
                console.warn('[useTextDocumentContent] Loaded document, but no paragraphs found for identity:', identity.id);
                setParagraphs([]); // Assuming empty array for no content found for a valid doc
                setRawParagraphs([]);
            }
            setError(null); // Clear previous error on new success
        }
        catch (e) {
            console.error('[useTextDocumentContent] Error loading document content for identity:', identity.id, e);
            if (e instanceof Error) {
                setError(e);
            }
            else {
                setError(new Error('An unknown error occurred while loading document content.'));
            }
            setParagraphs(null); // Clear paragraphs on error
            setRawParagraphs(null);
        }
        finally {
            setIsLoading(false);
        }
        // Depends on documentManager instance. Identity changes are handled via explicit call.
    }, [documentManager]);
    // Initial load or load on identity change can be triggered from the component using this hook
    // by calling the returned `loadContent` function when its `identity` prop changes.
    // This makes the hook more controllable from the outside.
    return useMemo(() => ({ paragraphs, rawParagraphs, isLoading, error, loadContent }), [paragraphs, rawParagraphs, isLoading, error, loadContent]);
}
//# sourceMappingURL=useTextDocumentContent.js.map